// Analytics Tracking Utility
// Integrates both Google Analytics (GA4) and Facebook Pixel
// Track key user actions and behaviors on vaulto.social

// Initialize gtag if it doesn't exist
window.gtag = window.gtag || function() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
};

// Initialize fbq if it doesn't exist
window.fbq = window.fbq || function() {
  window.fbq.queue = window.fbq.queue || [];
  window.fbq.queue.push(arguments);
};

// Event categories
export const EventCategories = {
  AUTH: 'Authentication',
  OAUTH: 'OAuth_Connection',
  BACKUP: 'Backup_Operations',
  SUBSCRIPTION: 'Subscription',
  USER_ENGAGEMENT: 'User_Engagement',
  ERROR: 'Error',
  NAVIGATION: 'Navigation',
  FEATURE_USAGE: 'Feature_Usage'
};

// Track generic events to both GA4 and Facebook Pixel
export const trackEvent = (action, category, label = null, value = null, fbEventName = null, fbEventParams = {}) => {
  try {
    // Google Analytics Event
    const gaEventParams = {
      event_category: category,
    };
    
    if (label) gaEventParams.event_label = label;
    if (value !== null) gaEventParams.value = value;
    
    if (typeof window.gtag === 'function') {
      window.gtag('event', action, gaEventParams);
    } else {
      console.warn('⚠️ Google Analytics not loaded');
    }
    
    // Facebook Pixel Event
    if (fbEventName) {
      if (typeof window.fbq === 'function') {
        window.fbq('track', fbEventName, fbEventParams);
        console.log('✅ Facebook Pixel Event Fired:', fbEventName, fbEventParams);
      } else {
        console.warn('⚠️ Facebook Pixel not loaded. Event not sent:', fbEventName, fbEventParams);
      }
    }
    
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', { 
        ga4: { action, category, label, value },
        fbPixel: fbEventName ? { event: fbEventName, params: fbEventParams } : null
      });
    }
  } catch (error) {
    console.error('❌ Error tracking event:', error);
  }
};

// Authentication Events
export const trackAuth = {
  signup: (method = 'onsite_form') => {
    try {
      // GA4 sign_up event with method parameter
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', {
          method: method === 'email' ? 'onsite_form' : method
        });
      }
      
      // Facebook Pixel CompleteRegistration event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'CompleteRegistration', {});
      }
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 GA4 sign_up event:', { method: method === 'email' ? 'onsite_form' : method });
      }
    } catch (error) {
      console.error('❌ Error tracking sign_up event:', error);
    }
  },
  
  login: (method = 'email') => {
    trackEvent('login', EventCategories.AUTH, method);
  },
  
  logout: () => {
    trackEvent('logout', EventCategories.AUTH);
  },
  
  passwordReset: () => {
    trackEvent('password_reset', EventCategories.AUTH);
  }
};

// OAuth Connection Events
export const trackOAuth = {
  connectInstagram: (success) => {
    try {
      if (success) {
        // GA4 ig_connected event with proper parameters
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'ig_connected', {
            integration: 'instagram',
            status: 'connected'
          });
        }
        
        // Facebook Pixel IGConnected event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'IGConnected', {});
        }
        
        // Log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 GA4 ig_connected event:', { integration: 'instagram', status: 'connected' });
        }
      } else {
        // Track failed connection using generic trackEvent
        trackEvent('instagram_connection_failed', EventCategories.OAUTH, 'failed');
      }
    } catch (error) {
      console.error('❌ Error tracking ig_connected event:', error);
    }
  },
  
  disconnectInstagram: () => {
    trackEvent('instagram_disconnected', EventCategories.OAUTH);
  },
  
  connectGoogleDrive: (success) => {
    try {
      if (success) {
        // GA4 drive_connected event with proper parameters
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'drive_connected', {
            integration: 'google_drive',
            status: 'connected'
          });
        }
        
        // Facebook Pixel DriveConnected event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'DriveConnected', {});
        }
        
        // Log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 GA4 drive_connected event:', { integration: 'google_drive', status: 'connected' });
        }
      } else {
        // Track failed connection using generic trackEvent
        trackEvent('google_drive_connection_failed', EventCategories.OAUTH, 'failed');
      }
    } catch (error) {
      console.error('❌ Error tracking drive_connected event:', error);
    }
  },
  
  disconnectGoogleDrive: () => {
    trackEvent('google_drive_disconnected', EventCategories.OAUTH);
  },
  
  userConnected: () => {
    try {
      // GA4 user_connected event with proper parameters
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'user_connected', {
          integrations_connected: ['instagram', 'google_drive'],
          total_integrations: 2
        });
      }
      
      // Facebook Pixel UserConnected event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'UserConnected', {});
      }
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 GA4 user_connected event:', { 
          integrations_connected: ['instagram', 'google_drive'], 
          total_integrations: 2 
        });
      }
    } catch (error) {
      console.error('❌ Error tracking user_connected event:', error);
    }
  },
  
  addInstagramAccount: (accountNumber) => {
    trackEvent('instagram_account_added', EventCategories.OAUTH, `account_${accountNumber}`);
  },
  
  removeInstagramAccount: (accountId) => {
    trackEvent('instagram_account_removed', EventCategories.OAUTH, accountId);
  }
};

// Backup Operations Events
export const trackBackup = {
  manualBackupStarted: () => {
    trackEvent('manual_backup_started', EventCategories.BACKUP);
  },
  
  manualBackupCompleted: (postsCount) => {
    trackEvent('manual_backup_completed', EventCategories.BACKUP, 'success', postsCount);
  },
  
  manualBackupFailed: (error) => {
    trackEvent('manual_backup_failed', EventCategories.BACKUP, error);
  },
  
  autoBackupEnabled: () => {
    trackEvent('auto_backup_enabled', EventCategories.BACKUP);
  },
  
  autoBackupDisabled: () => {
    trackEvent('auto_backup_disabled', EventCategories.BACKUP);
  },
  
  backupScheduleChanged: (schedule) => {
    trackEvent('backup_schedule_changed', EventCategories.BACKUP, schedule);
  },
  
  syncCompleted: (accountId, postsCount) => {
    trackEvent('sync_completed', EventCategories.BACKUP, accountId, postsCount);
  },
  
  duplicateHandlingChanged: (method) => {
    trackEvent('duplicate_handling_changed', EventCategories.BACKUP, method);
  }
};

// Subscription Events
export const trackSubscription = {
  viewPricing: () => {
    trackEvent('view_pricing', EventCategories.SUBSCRIPTION, null, null, 'ViewContent', {
      content_name: 'pricing_page',
      content_category: 'subscription'
    });
  },
  
  selectPlan: (planName, price) => {
    trackEvent('select_plan', EventCategories.SUBSCRIPTION, planName, price, 'AddToCart', {
      content_name: planName,
      content_type: 'product',
      value: price,
      currency: 'USD'
    });
  },
  
  startCheckout: (planName, price, itemId = null) => {
    try {
      // Build items array for GA4
      const items = [];
      if (itemId && planName && price !== null && price !== undefined) {
        items.push({
          item_id: itemId || planName,
          item_name: planName,
          currency: 'USD',
          price: price,
          quantity: 1
        });
      }
      
      // GA4 begin_checkout event with proper format
      const checkoutParams = {
        currency: 'USD'
      };
      
      if (price !== null && price !== undefined && price > 0) {
        checkoutParams.value = price;
      }
      
      if (items.length > 0) {
        checkoutParams.items = items;
      }
      
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'begin_checkout', checkoutParams);
      }
      
      // Facebook Pixel InitiateCheckout event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'InitiateCheckout', {
          content_name: planName,
          value: price || 0,
          currency: 'USD'
        });
      }
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 GA4 begin_checkout event:', checkoutParams);
      }
    } catch (error) {
      console.error('❌ Error tracking begin_checkout event:', error);
    }
  },
  
  purchase: (transactionId, planName, price, itemId = null) => {
    try {
      // Generate transaction ID if not provided
      const txId = transactionId || `vaulto_${Date.now()}`;
      
      // Build items array for GA4
      const items = [];
      if (itemId && planName && price !== null && price !== undefined) {
        items.push({
          item_id: itemId || planName,
          item_name: planName,
          currency: 'USD',
          price: price,
          quantity: 1
        });
      }
      
      // GA4 purchase event with proper format
      const purchaseParams = {
        transaction_id: txId,
        currency: 'USD',
        tax: 0,
        shipping: 0,
        coupon: ''
      };
      
      if (price !== null && price !== undefined && price > 0) {
        purchaseParams.value = price;
      }
      
      if (items.length > 0) {
        purchaseParams.items = items;
      }
      
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', purchaseParams);
      }
      
      // GA4 Conversion Event disabled
      
      // Facebook Pixel Purchase Event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          currency: 'USD',
          value: price || 0
        });
      }
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 GA4 purchase event:', purchaseParams);
      }
    } catch (error) {
      console.error('❌ Error tracking purchase event:', error);
    }
  },
  
  cancelSubscription: (planName) => {
    trackEvent('subscription_cancelled', EventCategories.SUBSCRIPTION, planName);
  },
  
  updatePaymentMethod: () => {
    trackEvent('payment_method_updated', EventCategories.SUBSCRIPTION);
  },
  
  startTrial: (campaignName, trialDays = 90, planName = 'Premium') => {
    try {
      // GA4 event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'start_trial', {
          campaign: campaignName,
          trial_days: trialDays,
          plan_name: planName,
          value: 0,
          currency: 'USD'
        });
      }
      
      // Facebook Pixel StartTrial event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'StartTrial', {
          content_name: planName,
          campaign: campaignName,
          trial_period: trialDays
        });
      }
      
      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Trial started event:', { campaignName, trialDays, planName });
      }
    } catch (error) {
      console.error('❌ Error tracking start_trial event:', error);
    }
  }
};

// User Engagement Events
export const trackEngagement = {
  viewDashboard: () => {
    trackEvent('view_dashboard', EventCategories.USER_ENGAGEMENT);
  },
  
  viewBackupHistory: () => {
    trackEvent('view_backup_history', EventCategories.USER_ENGAGEMENT);
  },
  
  viewSettings: () => {
    trackEvent('view_settings', EventCategories.USER_ENGAGEMENT);
  },
  
  viewHelpCenter: () => {
    trackEvent('view_help_center', EventCategories.USER_ENGAGEMENT);
  },
  
  contactSupport: (method) => {
    trackEvent('contact_support', EventCategories.USER_ENGAGEMENT, method);
  },
  
  shareApp: (method) => {
    trackEvent('share_app', EventCategories.USER_ENGAGEMENT, method);
  }
};

// Error Tracking
export const trackError = {
  apiError: (endpoint, errorCode) => {
    trackEvent('api_error', EventCategories.ERROR, `${endpoint}_${errorCode}`);
  },
  
  authError: (type, message) => {
    trackEvent('auth_error', EventCategories.ERROR, `${type}: ${message}`);
  },
  
  backupError: (type, message) => {
    trackEvent('backup_error', EventCategories.ERROR, `${type}: ${message}`);
  },
  
  paymentError: (type, message) => {
    trackEvent('payment_error', EventCategories.ERROR, `${type}: ${message}`);
  }
};

// Page View Tracking (for SPAs)
export const trackPageView = (pagePath, pageTitle) => {
  // GA4 Page View
  window.gtag('config', 'G-HKFZXN5WQX', {
    page_path: pagePath,
    page_title: pageTitle
  });
  
  // Facebook Pixel Page View
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
};

// User Properties (for user segmentation)
export const setUserProperties = (properties) => {
  window.gtag('set', 'user_properties', properties);
};

// Set User ID (for cross-device tracking)
export const setUserId = (userId) => {
  window.gtag('config', 'G-HKFZXN5WQX', {
    user_id: userId
  });
};

// Track timing (for performance monitoring)
export const trackTiming = (name, value, category = 'Performance') => {
  window.gtag('event', 'timing_complete', {
    name: name,
    value: value,
    event_category: category
  });
};

const analyticsExports = {
  trackEvent,
  trackAuth,
  trackOAuth,
  trackBackup,
  trackSubscription,
  trackEngagement,
  trackError,
  trackPageView,
  setUserProperties,
  setUserId,
  trackTiming
};

export default analyticsExports;
